import { useEffect, useState, type ReactNode } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { api } from "./api";
import { useAuthApi } from "./hooks/useAuthApi";
import { AUTH0_AUDIENCE, MGMT } from "./env";
import CreateTenant from "./views/CreateTenant";
import type { TenantUser } from "./types";

type Status = "loading" | "has-tenant" | "no-tenant" | "checkout-pending" | "checkout-timeout";

function getCheckoutParam(): "success" | "cancelled" | null {
  const params = new URLSearchParams(window.location.search);
  const v = params.get("checkout");
  if (v === "success" || v === "cancelled") return v;
  return null;
}

function clearCheckoutParam() {
  const url = new URL(window.location.href);
  url.searchParams.delete("checkout");
  window.history.replaceState(null, "", url.pathname + url.hash);
}

export default function TenantGuard({ children }: { children: ReactNode }) {
  const authApi = useAuthApi();
  const { getAccessTokenSilently } = useAuth0();
  const [status, setStatus] = useState<Status>("loading");

  const check = async () => {
    setStatus("loading");
    try {
      await authApi<TenantUser>(`${MGMT}/users/me`);
      setStatus("has-tenant");
    } catch (err) {
      const msg = (err as Error).message ?? "";
      if (msg.includes("not linked to any tenant")) {
        setStatus("no-tenant");
      } else {
        setStatus("has-tenant");
      }
    }
  };

  const pollForTenant = async () => {
    setStatus("checkout-pending");
    const maxAttempts = 20;
    for (let i = 0; i < maxAttempts; i++) {
      let token: string;
      try {
        token = await getAccessTokenSilently({
          authorizationParams: { audience: AUTH0_AUDIENCE || undefined },
          cacheMode: "off",
        });
      } catch {
        // Silent auth failed transiently (e.g. ITP, refresh token missing) — keep waiting
        await new Promise((r) => setTimeout(r, 3000));
        continue;
      }
      try {
        await api<TenantUser>(`${MGMT}/users/me`, {}, token);
        clearCheckoutParam();
        setStatus("has-tenant");
        return;
      } catch (err) {
        const msg = (err as Error).message ?? "";
        if (!msg.includes("not linked to any tenant") && !msg.includes("403")) {
          // Unexpected API error — stop polling
          break;
        }
        // Tenant not provisioned yet — wait and retry
      }
      await new Promise((r) => setTimeout(r, 3000));
    }
    clearCheckoutParam();
    setStatus("checkout-timeout");
  };

  useEffect(() => {
    const checkoutParam = getCheckoutParam();
    if (checkoutParam === "success") {
      pollForTenant();
    } else if (checkoutParam === "cancelled") {
      clearCheckoutParam();
      setStatus("no-tenant");
    } else {
      check();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "loading") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (status === "checkout-pending") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Setting up your organization…
        </Typography>
      </Box>
    );
  }

  if (status === "checkout-timeout") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
          p: 3,
        }}
      >
        <Alert severity="warning" sx={{ maxWidth: 480 }}>
          Your payment was received but your organization is taking longer than expected to set up.
          Please refresh the page in a moment or contact support if this persists.
        </Alert>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          Refresh
        </Button>
      </Box>
    );
  }

  if (status === "no-tenant") {
    return <CreateTenant />;
  }

  return <>{children}</>;
}
